import { Suspense, useState, type MouseEvent } from 'react';

import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';

import {
	faListUl,
	faRightFromBracket,
} from '@fortawesome/pro-duotone-svg-icons';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	AppBar,
	Box,
	Button,
	Container,
	Drawer,
	IconButton,
	LinearProgress,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Toolbar,
	Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import { useOidc } from '@axa-fr/react-oidc';
import { Toaster } from 'sonner';

import { useUser } from '@repo/auth';
import { PermissionsPanel } from '@repo/features/permissionsPanel';
import { ColorAvatar } from '@repo/ui/ColorAvatar';
import { UtilSVG } from '@repo/ui/UtilSVG';

import { ScrollContainer } from '~pages/-context/ScrollContainer';

export const Route = createFileRoute('/_authenticated')({
	component: function Auth() {
		const user = useUser();

		const navigate = useNavigate();

		const confirm = useConfirm();

		const [showNav, setShowNav] = useState(false);
		const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

		const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
			setAnchorElUser(event.currentTarget);
		};

		const handleCloseUserMenu = () => {
			setAnchorElUser(null);
		};

		const { logout } = useOidc();

		return (
			<>
				<UtilSVG />
				<PermissionsPanel />
				<AppBar
					position="sticky"
					sx={{ backgroundColor: 'white', color: '#222' }}
				>
					<Container maxWidth="lg" sx={{ '&&': { paddingLeft: 0 } }}>
						<Toolbar disableGutters sx={{ gap: 2 }}>
							<Box sx={{ flexGrow: 1 }}>
								<Button
									variant="text"
									disableRipple
									sx={{
										'&:hover': {
											backgroundColor: 'transparent',
										},
									}}
									onClick={() => {
										void navigate({ to: '/' });
									}}
									aria-label="MDDi"
								>
									<img style={{ height: '55px' }} src="/MDDi.png" alt="" />
								</Button>
							</Box>
							<Button
								onClick={() => {
									setShowNav(false);
									void navigate({
										to: '/',
									});
								}}
								variant="outlined"
								startIcon={<FontAwesomeIcon icon={faListUl} />}
							>
								Claims
							</Button>
							<ColorAvatar
								user={user}
								onClick={(event) => {
									handleOpenUserMenu(event);
								}}
							/>
							<Menu
								sx={{ mt: 0.5 }}
								id="menu-appbar"
								anchorEl={anchorElUser}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								keepMounted
								transformOrigin={{
									vertical: 'top',
									horizontal: 'right',
								}}
								open={Boolean(anchorElUser)}
								onClose={handleCloseUserMenu}
								onKeyUp={(event) => {
									if (event.key === '!') {
										user.permissions.toggleActive();
										handleCloseUserMenu();
									}
								}}
								slotProps={{
									list: {
										sx: { paddingTop: 0 },
									},
								}}
							>
								<Typography
									variant="caption"
									sx={{
										display: 'block',
										position: 'sticky',
										top: '-8px',
										padding: '4px 10px',
										width: '100%',
										backgroundColor: 'accent.light',
										zIndex: 100,
										'& + ul': { padding: 0 },
									}}
								>
									{user.firstName} {user.lastName}
								</Typography>
								<MenuItem
									onClick={() => {
										handleCloseUserMenu();
										void confirm({
											title: 'Log Out',
											description: 'Are you sure you want to log out?',
											cancellationText: 'No',
											confirmationText: 'Yes',
										}).then(({ confirmed }) => {
											if (confirmed) {
												void logout();
											}
										});
									}}
								>
									<ListItemIcon>
										<FontAwesomeIcon
											icon={faRightFromBracket}
											size="sm"
											fixedWidth
										/>
									</ListItemIcon>
									<ListItemText>
										<Typography>Log Out</Typography>
									</ListItemText>
								</MenuItem>
							</Menu>
							<Box
								sx={{
									display: { xs: 'flex', md: 'none' },
									alignItems: 'center',
									justifyContent: 'flex-end',
								}}
							>
								<IconButton
									size="large"
									aria-label="application menu"
									aria-controls="menu-appbar"
									aria-haspopup="true"
									onClick={() => {
										setShowNav((prev) => !prev);
									}}
									color="inherit"
								>
									<FontAwesomeIcon icon={faBars} />
								</IconButton>
							</Box>
						</Toolbar>
					</Container>
				</AppBar>
				<Drawer
					open={showNav}
					onClose={() => {
						setShowNav(false);
					}}
					anchor="right"
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							justifyContent: 'flex-end',
							transition: 'height 0.25s ease-in',
							pt: 2,
						}}
					>
						<MenuItem
							onClick={() => {
								setShowNav(false);
								void navigate({
									to: '/',
								});
							}}
						>
							<ListItemIcon>
								<FontAwesomeIcon icon={faListUl} size="sm" fixedWidth />
							</ListItemIcon>
							<ListItemText>
								<Typography>Claims</Typography>
							</ListItemText>
						</MenuItem>
					</Box>
				</Drawer>
				<Toaster richColors position="top-center" />
				<Box
					component="main"
					sx={{
						position: 'relative',
						flex: '1',
						display: 'flex',
						flexDirection: 'column',
						maxHeight: 'calc(100dvh - 69px)',
					}}
				>
					<ScrollContainer>
						<Suspense fallback={<LinearProgress />}>
							<Outlet />
						</Suspense>
					</ScrollContainer>
				</Box>
			</>
		);
	},
});
